import { lazy, type ComponentType } from 'react';

export type UserRole = 'OPERATOR' | 'STORE_MANAGER' | 'ADMIN' | 'SUPER_ADMIN';

export type WidgetSize = 'xs' | 's' | 'm' | 'l' | 'xl';

export type WidgetCategory =
  | 'general'
  | 'finance'
  | 'kassza'
  | 'inventory'
  | 'service'
  | 'partner'
  | 'alerts'
  | 'rental';

export interface WidgetConfig {
  id: string;
  component: ComponentType<Record<string, unknown>>;
  roles: UserRole[]; // Empty array = all roles can see
  category: WidgetCategory;
  size: WidgetSize;
  refreshInterval: number | null; // seconds, null = no auto-refresh
}

/**
 * Widget size to grid column span mapping (12-column grid)
 * Desktop (lg+): XS=3, S=4, M=6, L=8, XL=12
 * Tablet (md): XS/S/M=6, L/XL=12
 * Mobile: all=12
 */
export const WIDGET_SIZE_TO_COLS: Record<WidgetSize, { desktop: number; tablet: number }> = {
  xs: { desktop: 3, tablet: 6 },
  s: { desktop: 4, tablet: 6 },
  m: { desktop: 6, tablet: 6 },
  l: { desktop: 8, tablet: 12 },
  xl: { desktop: 12, tablet: 12 },
};

/**
 * Tailwind col-span class mapping (must use full class names for JIT)
 */
export const WIDGET_SIZE_CLASSES: Record<WidgetSize, string> = {
  xs: 'col-span-12 md:col-span-6 lg:col-span-3',
  s: 'col-span-12 md:col-span-6 lg:col-span-4',
  m: 'col-span-12 md:col-span-6 lg:col-span-6',
  l: 'col-span-12 md:col-span-12 lg:col-span-8',
  xl: 'col-span-12',
};

// Widget Registry - centralized lazy loading with role-based filtering
export const WIDGET_REGISTRY: Record<string, Omit<WidgetConfig, 'id'>> = {
  'welcome-card': {
    component: lazy(() => import('@kgc/ui').then(m => ({ default: m.WelcomeCard }))),
    roles: [], // All roles can see
    category: 'general',
    size: 'xl',
    refreshInterval: null,
  },
  'empty-state': {
    component: lazy(() => import('@kgc/ui').then(m => ({ default: m.EmptyStateWidget }))),
    roles: [], // All roles can see
    category: 'general',
    size: 'xl',
    refreshInterval: null,
  },
  // Financial KPI Widgets (Story 35-2) - with data fetching wrappers
  'revenue-kpi': {
    component: lazy(() => import('../widgets/RevenueKPIWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'finance',
    size: 's',
    refreshInterval: 300, // 5 minutes
  },
  'net-revenue-kpi': {
    component: lazy(() => import('../widgets/NetRevenueKPIWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'finance',
    size: 's',
    refreshInterval: 300, // 5 minutes
  },
  'receivables-kpi': {
    component: lazy(() => import('../widgets/ReceivablesKPIWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'finance',
    size: 's',
    refreshInterval: 300, // 5 minutes
  },
  'payments-kpi': {
    component: lazy(() => import('../widgets/PaymentsKPIWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'finance',
    size: 's',
    refreshInterval: 300, // 5 minutes
  },

  // Inventory Stock Widgets (Story 35-3) - with data fetching wrappers
  'stock-summary': {
    component: lazy(() => import('../widgets/StockSummaryWidget')),
    roles: ['OPERATOR', 'STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'inventory',
    size: 'm',
    refreshInterval: 300, // 5 minutes
  },
  'stock-utilization': {
    component: lazy(() => import('../widgets/StockUtilizationWidget')),
    roles: ['OPERATOR', 'STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'inventory',
    size: 'm',
    refreshInterval: 300, // 5 minutes
  },
  'stock-alerts': {
    component: lazy(() => import('../widgets/StockAlertListWidget')),
    roles: ['OPERATOR', 'STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'inventory',
    size: 's',
    refreshInterval: 300, // 5 minutes
  },
  'stock-movement': {
    component: lazy(() => import('../widgets/StockMovementChartWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'inventory',
    size: 'm',
    refreshInterval: 300, // 5 minutes
  },
  'stock-heatmap': {
    component: lazy(() => import('../widgets/StockHeatmapWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'inventory',
    size: 'l',
    refreshInterval: 300, // 5 minutes
  },

  // Notification Panel Widget (Story 35-4)
  'notification-panel': {
    component: lazy(() => import('../widgets/NotificationPanelWidget')),
    roles: [], // All roles can see notifications
    category: 'alerts',
    size: 'm',
    refreshInterval: 300, // 5 minutes (managed by TanStack Query polling)
  },

  // Service Dashboard Widgets (Story 35-5)
  'worksheet-summary': {
    component: lazy(() => import('../widgets/WorksheetSummaryWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'service',
    size: 'm',
    refreshInterval: 300, // 5 minutes
  },
  'technician-workload': {
    component: lazy(() => import('../widgets/TechnicianWorkloadWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'service',
    size: 'm',
    refreshInterval: 300, // 5 minutes
  },
  'service-revenue': {
    component: lazy(() => import('../widgets/ServiceRevenueWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'service',
    size: 's',
    refreshInterval: 300, // 5 minutes
  },
  'warranty-ratio-placeholder': {
    component: lazy(() => import('../widgets/WarrantyRatioPlaceholder')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'service',
    size: 's',
    refreshInterval: null, // Placeholder - no refresh
  },

  // Partner Dashboard Widgets (Story 35-6)
  'partner-overview': {
    component: lazy(() => import('../widgets/PartnerOverviewWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'partner',
    size: 'm',
    refreshInterval: 300, // 5 minutes
  },
  'top-partners': {
    component: lazy(() => import('../widgets/TopPartnersWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'partner',
    size: 'm',
    refreshInterval: 300, // 5 minutes
  },
  'partner-activity': {
    component: lazy(() => import('../widgets/PartnerActivityWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'partner',
    size: 's',
    refreshInterval: 300, // 5 minutes
  },
  'partner-credit-placeholder': {
    component: lazy(() => import('../widgets/PartnerCreditPlaceholder')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'partner',
    size: 's',
    refreshInterval: null, // Placeholder - no refresh
  },

  // Equipment Profit Widget (Story 40-4)
  'equipment-profit': {
    component: lazy(() => import('../widgets/EquipmentProfitWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'finance',
    size: 'l',
    refreshInterval: 300, // 5 minutes
  },

  // Receivables Aging Widget (Story 41-1)
  'receivables-aging': {
    component: lazy(() => import('../widgets/ReceivablesAgingWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'finance',
    size: 'l',
    refreshInterval: 300, // 5 minutes
  },

  // Revenue Forecast Widget (Story 41-2)
  'revenue-forecast': {
    component: lazy(() => import('../widgets/RevenueForecastWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'finance',
    size: 'm',
    refreshInterval: 300, // 5 minutes
  },

  // Epic 48: Rental Dashboard Widgets (renamed from analytics → rental)
  'rental-stats': {
    component: lazy(() => import('../widgets/RentalStatsWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'rental',
    size: 'm',
    refreshInterval: 300, // 5 minutes
  },
  'popular-equipment': {
    component: lazy(() => import('../widgets/PopularEquipmentWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'rental',
    size: 'm',
    refreshInterval: 300, // 5 minutes
  },
  'seasonality-chart': {
    component: lazy(() => import('../widgets/SeasonalityChartWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'rental',
    size: 'l',
    refreshInterval: 300, // 5 minutes
  },

  // Epic 49: Service Dashboard Extensions
  'service-profit': {
    component: lazy(() => import('../widgets/ServiceProfitWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'service',
    size: 'm',
    refreshInterval: 300, // 5 minutes
  },
  'recurring-issues': {
    component: lazy(() => import('../widgets/RecurringIssuesWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'service',
    size: 'm',
    refreshInterval: 300, // 5 minutes
  },
  'warranty-ratio': {
    component: lazy(() => import('../widgets/WarrantyRatioWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'service',
    size: 's',
    refreshInterval: 300, // 5 minutes
  },

  // Epic 46: VAT Summary Widget (Story 46-6)
  'vat-summary': {
    component: lazy(() => import('../widgets/VatSummaryWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'finance',
    size: 'm',
    refreshInterval: 300, // 5 minutes
  },

  // Epic 46: Card Expiration Widget (Story 46-13)
  'card-expiration': {
    component: lazy(() => import('../widgets/CardExpirationWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'alerts',
    size: 's',
    refreshInterval: 300, // 5 minutes
  },

  // Epic 74: Kassza Dashboard Widgets (Story 74-3)
  'kassza-three-pocket': {
    component: lazy(() => import('../widgets/KasszaThreePocketWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'kassza',
    size: 'l',
    refreshInterval: 60, // 1 minute - kassza érzékeny adat
  },
  'kassza-tolerance': {
    component: lazy(() => import('../widgets/KasszaToleranceWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'kassza',
    size: 's',
    refreshInterval: 60,
  },
  'kassza-transactions': {
    component: lazy(() => import('../widgets/KasszaTransactionsWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'kassza',
    size: 'm',
    refreshInterval: 60,
  },
  'kassza-daily-status': {
    component: lazy(() => import('../widgets/KasszaDailyStatusWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'kassza',
    size: 'xs',
    refreshInterval: 60,
  },
  'kassza-szh-report': {
    component: lazy(() => import('../widgets/KasszaSzhReportWidget')),
    roles: ['STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'kassza',
    size: 'xs',
    refreshInterval: 60,
  },

  // Epic MFG: Consignment Summary Widget (M-4)
  'consignment-summary': {
    component: lazy(() =>
      import('../../../pages/dashboard/widgets/ConsignmentSummaryWidget').then(m => ({
        default: m.ConsignmentSummaryWidget,
      }))
    ),
    roles: ['OPERATOR', 'STORE_MANAGER', 'ADMIN', 'SUPER_ADMIN'],
    category: 'inventory',
    size: 's',
    refreshInterval: 300,
  },
};

/**
 * Get all widgets that the given user role can access
 * Widgets with empty roles array are visible to all roles
 */
export function getWidgetsByRole(role: UserRole | null): WidgetConfig[] {
  return Object.entries(WIDGET_REGISTRY)
    .filter(([, config]) => {
      // Empty roles = all roles can see
      if (config.roles.length === 0) return true;
      // `null` = a szerep termék-döntésre vár (QA-100 ③): csak a szerep-független
      // widgetek jelennek meg. Ez UI-láthatóság, nem hozzáférés-védelem.
      if (role === null) return false;
      // Check if user role is in widget's allowed roles
      return config.roles.includes(role);
    })
    .map(([id, config]) => ({
      id,
      ...config,
    }));
}

/**
 * Get widget configuration by widget ID
 * Returns undefined if widget doesn't exist
 */
export function getWidgetById(widgetId: string): WidgetConfig | undefined {
  const config = WIDGET_REGISTRY[widgetId];
  if (!config) return undefined;

  return {
    id: widgetId,
    ...config,
  };
}

/**
 * Pre-computed widgets grouped by category
 *
 * Performance: Computed once at module load to avoid repeated
 * Object.entries() iterations in hooks and components.
 */
export const WIDGETS_BY_CATEGORY: Map<WidgetCategory, WidgetConfig[]> = (() => {
  const map = new Map<WidgetCategory, WidgetConfig[]>();

  Object.entries(WIDGET_REGISTRY).forEach(([id, config]) => {
    const widgetConfig: WidgetConfig = { id, ...config };
    const existing = map.get(config.category) ?? [];
    map.set(config.category, [...existing, widgetConfig]);
  });

  return map;
})();

/**
 * Get widgets by category (uses pre-computed map)
 *
 * Performance: O(1) lookup instead of O(n) iteration
 */
export function getWidgetsByCategory(category: WidgetCategory): WidgetConfig[] {
  return WIDGETS_BY_CATEGORY.get(category) ?? [];
}
